//type
import type { ComponentProps } from 'react';

import { Listbox } from '@headlessui/react';
import clsx from 'clsx';
import { isNil } from 'lodash';

import type { OptionType } from '@/components/base/Select/SelectComponent/types';

//components
import Checkbox from '@/components/base/Checkbox';
import type { SelectOptionProps } from '@/components/base/Select/SelectComponent/SubComponents/SelectOption';

type WrapperProps<TOption, TSelected> = ComponentProps<'div'> &
    Partial<
        Pick<SelectOptionProps<TOption, TSelected>, 'isMultiple' | 'isTruncate' | 'forcedDisplay'>
    > &
    Omit<ComponentProps<typeof Listbox.Option>, 'value'> & {
        selected: boolean;
        option: TOption;
    };

function Wrapper<TOption extends OptionType, TSelected extends TOption | TOption[]>({
    option,
    selected,
    disabled,
    forcedDisplay,
    isTruncate = true,
    isMultiple = false,
    children,
    ...restProps
}: WrapperProps<TOption, TSelected>) {
    const noValue = isNil(option?.value ?? option?.label);
    return (
        <div
            className={clsx('focus inset-y-0 block', {
                'font-GBold text-icoBlue': selected && !isMultiple,
                'font-GSemiBold': !selected || isMultiple,
                'text-icoBlack dark:text-white': isMultiple,
                'text-icoGray-400': disabled || (noValue && !selected && !forcedDisplay),
                truncate: isTruncate
            })}
            {...restProps}>
            {children}
        </div>
    );
}

type DefaultSelectOptionProps<TOption, TSelected> = Pick<
    SelectOptionProps<TOption, TSelected>,
    'isMultiple' | 'isTruncate' | 'forcedDisplay'
> &
    Omit<ComponentProps<typeof Listbox.Option>, 'value'> & {
        selected: boolean;
        option: TOption;
    };
export function DefaultSelectOption<
    TOption extends OptionType,
    TSelected extends TOption | TOption[]
>({
    option,
    selected,
    disabled,
    forcedDisplay,
    isTruncate = true,
    isMultiple = false
}: DefaultSelectOptionProps<TOption, TSelected>) {
    if (isMultiple) {
        return (
            <Wrapper
                option={option}
                selected={selected}
                disabled={disabled}
                forcedDisplay={forcedDisplay}
                isTruncate={isTruncate}
                isMultiple={isMultiple}>
                <span className="flex min-w-full items-center space-x-4">
                    <Checkbox checked={selected} disabled={disabled} readOnly />
                    <span title={option?.label}>{option?.label}</span>
                </span>
            </Wrapper>
        );
    }
    return (
        <Wrapper
            option={option}
            selected={selected}
            disabled={disabled}
            forcedDisplay={forcedDisplay}
            isTruncate={isTruncate}>
            <span title={option?.label}>{option?.label}</span>
        </Wrapper>
    );
}
